import { useCallback, useMemo } from "react";
import { vertebraGroups } from "../../../classes/enums/AnnotationsEnum";
import {
  ActNatures,
  OsteotomyTypes,
} from "../../../classes/enums/SurgicalActsEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";

/**
 * Hook for managing vertebra range selection logic.
 * Provides handlers and computed values for selecting vertebra ranges
 * used by both Rod.js (dropdowns) and RodsSelector.js (click selection).
 *
 * @param {Array} rods - Array of rod objects with selection state
 * @param {Function} onUpdateRod - Callback to update a rod by id
 * @param {Object} surgicalActs - Current surgical acts for exclusion logic
 * @param {Object} anatomyProfile - L6/T13 anatomy availability
 * @returns {Object} Handlers and computed values for vertebra selection
 */
const useVertebraRangeSelection = (
  rods,
  onUpdateRod,
  surgicalActs = {},
  anatomyProfile = {}
) => {
  const { hasL6 = false, hasT13 = false } = anatomyProfile;

  // Get vertebrae that have both L+R screws occupied by OTHER completed rods
  const getOccupiedVertebrae = useCallback(
    (excludingRodId = null) => {
      const occupied = new Set();
      const leftOccupied = new Set();
      const rightOccupied = new Set();

      rods.forEach(rod => {
        // Skip the rod we're excluding (the one being edited)
        if (rod.id === excludingRodId) return;
        // Only count completed rods (2+ vertebrae in selection)
        const screwCount = new Set([
          ...(rod.selectedLeftScrews || []),
          ...(rod.selectedRightScrews || []),
        ]).size;
        if (screwCount < 2) return;

        (rod.selectedLeftScrews || []).forEach(v => leftOccupied.add(v));
        (rod.selectedRightScrews || []).forEach(v => rightOccupied.add(v));
      });

      // A vertebra is fully occupied if it has both left AND right screws
      leftOccupied.forEach(v => {
        if (rightOccupied.has(v)) {
          occupied.add(v);
        }
      });

      return { occupied, leftOccupied, rightOccupied };
    },
    [rods]
  );

  // Build vertebra items list
  const vertebraItems = useMemo(
    () =>
      [
        "-",
        ...vertebraUtils.getAvailableVertebrae(hasL6, hasT13),
        ...vertebraGroups.SACRUM,
      ].map(vertebra => ({
        value: vertebra,
        label: vertebra === "-" ? "-" : vertebraUtils.getVertebraLabel(vertebra),
      })),
    [hasL6, hasT13]
  );

  // Extract ordered vertebra values (excluding placeholder)
  const vertebraOrder = useMemo(
    () =>
      vertebraItems
        .map(item => item.value)
        .filter(value => value && value !== "-"),
    [vertebraItems]
  );

  // Determine which vertebrae should be excluded from selection
  // Note: Rods are NOT excluded - they are loaded into the rods state for editing
  const getExcludedVertebrae = useCallback(acts => {
    const excluded = new Set();

    Object.values(acts).forEach(act => {
      if (act.actNature === ActNatures.Osteotomy) {
        // Only exclude vertebrae with Grade 3 (PSO) and Grade 4 (PSO) osteotomies
        const osteotomyType = act.details?.osteotomyType || act.osteotomyType;
        if (
          osteotomyType === OsteotomyTypes.GRADE_3_PSO ||
          osteotomyType === OsteotomyTypes.GRADE_4_PSO
        ) {
          excluded.add(act.vertebra);
        }
      }
      // Rods are intentionally not excluded - they are loaded into the editor
    });

    return excluded;
  }, []);

  const excludedVertebrae = useMemo(
    () => getExcludedVertebrae(surgicalActs),
    [surgicalActs, getExcludedVertebrae]
  );

  // Filter vertebra items to exclude unavailable ones
  const availableVertebraItems = useMemo(() => {
    return vertebraItems.filter(
      item => item.value === "-" || !excludedVertebrae.has(item.value)
    );
  }, [vertebraItems, excludedVertebrae]);

  // Get items for first dropdown - only vertebrae with at least one free neighbor
  const getFirstDropdownItems = useCallback(
    rod => {
      const { occupied } = getOccupiedVertebrae(rod?.id);
      const available = availableVertebraItems.filter(
        item => item.value !== "-"
      );

      return availableVertebraItems.filter(item => {
        if (item.value === "-") return true;
        if (occupied.has(item.value)) return false;

        const idx = available.findIndex(v => v.value === item.value);
        if (idx === -1) return false;

        // Check if there's at least one free neighbor above or below
        const hasAbove =
          idx > 0 && !occupied.has(available[idx - 1].value);
        const hasBelow =
          idx < available.length - 1 &&
          !occupied.has(available[idx + 1].value);

        return hasAbove || hasBelow;
      });
    },
    [availableVertebraItems, getOccupiedVertebrae]
  );

  // Get items for second dropdown - only contiguous vertebrae from first
  const getSecondDropdownItems = useCallback(
    (rod, firstVertebra) => {
      if (!firstVertebra || firstVertebra === "-") {
        return [{ value: "-", label: "-" }];
      }

      const { occupied } = getOccupiedVertebrae(rod?.id);
      const available = availableVertebraItems.filter(
        item => item.value !== "-"
      );
      const firstIdx = available.findIndex(v => v.value === firstVertebra);
      if (firstIdx === -1) return [{ value: "-", label: "-" }];

      const contiguous = [];

      // Go up from first vertebra until we hit an occupied one
      for (let i = firstIdx - 1; i >= 0; i--) {
        if (occupied.has(available[i].value)) break;
        contiguous.unshift(available[i]);
      }

      // Go down from first vertebra until we hit an occupied one
      for (let i = firstIdx + 1; i < available.length; i++) {
        if (occupied.has(available[i].value)) break;
        contiguous.push(available[i]);
      }

      // Filter out the first vertebra itself
      return [
        { value: "-", label: "-" },
        ...contiguous.filter(item => item.value !== firstVertebra),
      ];
    },
    [availableVertebraItems, getOccupiedVertebrae]
  );

  // Get selectable vertebrae for click selection
  const getSelectableVertebrae = useCallback(
    (rod, firstVertebra) => {
      if (!firstVertebra || firstVertebra === "-") {
        // When no first vertebra, return all valid first choices
        const items = getFirstDropdownItems(rod);
        return new Set(items.map(i => i.value).filter(v => v !== "-"));
      }

      // When first is selected, return contiguous options
      const items = getSecondDropdownItems(rod, firstVertebra);
      return new Set(items.map(i => i.value).filter(v => v !== "-"));
    },
    [getFirstDropdownItems, getSecondDropdownItems]
  );

  // Check side availability for a range
  const getSideAvailability = useCallback(
    (range, rodId) => {
      if (!range || range.length < 2) {
        return { leftAvailable: true, rightAvailable: true };
      }

      const { leftOccupied, rightOccupied } = getOccupiedVertebrae(rodId);

      // Count how many vertebrae in range have available left/right spots
      let leftAvailableCount = 0;
      let rightAvailableCount = 0;

      range.forEach(v => {
        if (!leftOccupied.has(v)) leftAvailableCount++;
        if (!rightOccupied.has(v)) rightAvailableCount++;
      });

      return {
        leftAvailable: leftAvailableCount >= 2,
        rightAvailable: rightAvailableCount >= 2,
      };
    },
    [getOccupiedVertebrae]
  );

  // Get the current range for a rod from its state
  const getRangeForRod = useCallback(
    rod => {
      const baseRange = rod.range && rod.range.length > 0 ? rod.range : null;
      const source = baseRange
        ? new Set(baseRange)
        : new Set([
            ...(rod.selectedLeftScrews || []),
            ...(rod.selectedRightScrews || []),
          ]);
      return vertebraOrder.filter(id => source.has(id));
    },
    [vertebraOrder]
  );

  // Apply a range to the active sides of a rod
  const applyRangeToActiveSides = useCallback(
    (rodId, range) => {
      // Get occupied vertebrae to check for overlaps
      const { leftOccupied, rightOccupied } = getOccupiedVertebrae(rodId);

      // Check if ANY vertebra in range overlaps with existing screws on each side
      // If there's ANY overlap, skip the entire side
      const leftHasOverlap = range.some(v => leftOccupied.has(v));
      const rightHasOverlap = range.some(v => rightOccupied.has(v));

      onUpdateRod(rodId, prev => {
        const hadLeft = (prev.selectedLeftScrews || []).length > 0;
        const hadRight = (prev.selectedRightScrews || []).length > 0;
        const defaultEnableBoth = !hadLeft && !hadRight;

        // Only enable a side if:
        // - It was previously enabled AND has no overlap, OR
        // - Default case (new rod) AND has no overlap
        const leftEnabled = (hadLeft || defaultEnableBoth) && !leftHasOverlap;
        const rightEnabled = (hadRight || defaultEnableBoth) && !rightHasOverlap;

        return {
          ...prev,
          range,
          selectedLeftScrews: leftEnabled ? range : [],
          selectedRightScrews: rightEnabled ? range : [],
        };
      });
    },
    [onUpdateRod, getOccupiedVertebrae]
  );

  // Handle first vertebra selection (start of range)
  // This stores a pending first vertebra - doesn't commit to screw arrays yet
  const handleFirstVertebraChange = useCallback(
    (rod, selectedItem) => {
      // Validate rod object
      if (!rod || !rod.id) {
        console.error("[useVertebraRangeSelection] handleFirstVertebraChange called with invalid rod:", rod);
        return;
      }

      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value !== undefined
          ? selectedItem.value
          : selectedItem;

      if (value === "-" || !value) {
        onUpdateRod(rod.id, {
          range: [],
          selectedLeftScrews: [],
          selectedRightScrews: [],
          pendingFirstVertebra: null,
        });
        return;
      }

      // Store as pending - don't apply to screw arrays until second vertebra is selected
      // This ensures we never have a single-vertebra rod in the committed state
      onUpdateRod(rod.id, {
        pendingFirstVertebra: value,
        // Clear any existing range when starting new selection
        range: [],
        selectedLeftScrews: [],
        selectedRightScrews: [],
      });
    },
    [onUpdateRod]
  );

  // Handle second vertebra selection (end of range)
  // This commits the full range (first + second) to screw arrays
  const handleSecondVertebraChange = useCallback(
    (rod, selectedItem) => {
      // Validate rod object
      if (!rod || !rod.id) {
        console.error("[useVertebraRangeSelection] handleSecondVertebraChange called with invalid rod:", rod);
        return;
      }

      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value !== undefined
          ? selectedItem.value
          : selectedItem;

      if (value === "-") {
        onUpdateRod(rod.id, {
          range: [],
          selectedLeftScrews: [],
          selectedRightScrews: [],
          pendingFirstVertebra: null,
        });
        return;
      }

      // Get first vertebra from pending state or existing range
      const firstVertebra = rod.pendingFirstVertebra || getRangeForRod(rod)[0];
      if (!firstVertebra || firstVertebra === "-") {
        console.warn("[useVertebraRangeSelection] No first vertebra found for rod:", rod.id);
        // Don't clear state - just return without action
        return;
      }

      const firstIndex = availableVertebraItems.findIndex(
        item => item.value === firstVertebra
      );
      const secondIndex = availableVertebraItems.findIndex(
        item => item.value === value
      );

      if (firstIndex === -1 || secondIndex === -1) {
        onUpdateRod(rod.id, {
          range: [],
          selectedLeftScrews: [],
          selectedRightScrews: [],
          pendingFirstVertebra: null,
        });
        return;
      }

      const startIndex = Math.min(firstIndex, secondIndex);
      const endIndex = Math.max(firstIndex, secondIndex);

      const vertebraRange = availableVertebraItems
        .slice(startIndex, endIndex + 1)
        .map(item => item.value)
        .filter(vertebra => vertebra !== "-");

      // Ensure we have at least 2 vertebrae
      if (vertebraRange.length < 2) {
        console.warn("[useVertebraRangeSelection] Range must have at least 2 vertebrae");
        return;
      }

      const finalRange =
        secondIndex < firstIndex ? vertebraRange.reverse() : vertebraRange;

      // Clear pending and apply the committed range
      onUpdateRod(rod.id, prev => ({
        ...prev,
        pendingFirstVertebra: null,
      }));
      applyRangeToActiveSides(rod.id, finalRange);
    },
    [availableVertebraItems, getRangeForRod, onUpdateRod, applyRangeToActiveSides]
  );

  // Get the first selected vertebra item for a rod
  const getSelectedVertebraItem = useCallback(
    rod => {
      // Check pending first vertebra first
      if (rod.pendingFirstVertebra) {
        const foundItem = vertebraItems.find(item => item.value === rod.pendingFirstVertebra);
        return foundItem || vertebraItems[0];
      }
      // Then check committed range
      const range = getRangeForRod(rod);
      const firstVertebra = range.length > 0 ? range[0] : null;
      if (!firstVertebra || firstVertebra === "-") {
        return vertebraItems[0];
      }
      const foundItem = vertebraItems.find(item => item.value === firstVertebra);
      return foundItem || vertebraItems[0];
    },
    [getRangeForRod, vertebraItems]
  );

  // Get the last selected vertebra item for a rod
  const getLastSelectedVertebraItem = useCallback(
    rod => {
      // If we only have pending first, second is not set yet
      if (rod.pendingFirstVertebra) {
        return vertebraItems[0]; // Return placeholder
      }
      const range = getRangeForRod(rod);
      const lastVertebra = range.length > 1 ? range[range.length - 1] : null;
      if (!lastVertebra || lastVertebra === "-") {
        return vertebraItems[0];
      }
      const foundItem = vertebraItems.find(item => item.value === lastVertebra);
      return foundItem || vertebraItems[0];
    },
    [getRangeForRod, vertebraItems]
  );

  return {
    // Data
    vertebraItems,
    vertebraOrder,
    availableVertebraItems,
    excludedVertebrae,
    // Handlers
    handleFirstVertebraChange,
    handleSecondVertebraChange,
    // Helpers
    getRangeForRod,
    applyRangeToActiveSides,
    getSelectedVertebraItem,
    getLastSelectedVertebraItem,
    // Filtering
    getOccupiedVertebrae,
    getFirstDropdownItems,
    getSecondDropdownItems,
    getSelectableVertebrae,
    getSideAvailability,
  };
};

export default useVertebraRangeSelection;
